import React from 'react';

import { useTransferGroupStreams } from '@vlabs/api/artemy/client';
import { StraightArrowUpAndDownIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { TransferStreamsForm } from './TransferStreamsForm';
import { useTransferStreamsTranslation } from '../i18n';
import type { TransferStreamsProps } from '../types';

const displayName = 'TransferStreamsButton';
const testid = buildTestid(displayName);

interface TransferStreamsButtonProps extends TransferStreamsProps, WithDataTestid, ButtonProps {
  /** В режиме отложенного переноса форма отдаёт выбранную группу наружу вместо вызова API. */
  onTransfer?: (targetGroupId: string) => void;
}

function TransferStreamsButton({
  streamIds,
  onSuccess = undefined,
  onTransfer = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: TransferStreamsButtonProps): React.JSX.Element {
  const { t } = useTransferStreamsTranslation();
  const modal = useModal({ size: 'small' });

  const transferGroupStreams = useTransferGroupStreams({
    mutation: {
      onSuccess: () => {
        toast.success(t('on_success'));
        modal.close();
        onSuccess?.();
      },
      onError: () => toast.error(t('on_error')),
    },
  });

  const handleTransfer = (targetGroupId: string): void => {
    if (onTransfer) {
      onTransfer(targetGroupId);
      modal.close();
      return;
    }

    transferGroupStreams.mutate({
      targetGroupId,
      data: { stream_ids: streamIds },
    });
  };

  return (
    <React.Fragment>
      {modal.wrap(
        <TransferStreamsForm
          isPending={transferGroupStreams.isPending}
          onCancel={modal.close}
          onTransfer={handleTransfer}
        />,
      )}

      <Button
        icon={<StraightArrowUpAndDownIcon />}
        onClick={modal.open}
        {...testid(dataTestId)}
        {...props}
      >
        {t('title')}
      </Button>
    </React.Fragment>
  );
}

TransferStreamsButton.displayName = displayName;

export { TransferStreamsButton };
